import { useCallback, useEffect, useState } from 'react'

/**
 * The signed-in user's enabled feature flags, fetched once per session identity.
 *
 * Pass the session user id (or null when signed out) so the set is refetched on sign-in and
 * sign-out. Everything reads as off while loading and after a failed fetch: gated UI stays
 * hidden rather than flashing in. No polling; a flag change reaches a user on their next load.
 */
export function useFeatureFlags(userId: string | null | undefined): {
  isEnabled: (key: string) => boolean
  isLoading: boolean
} {
  const [enabled, setEnabled] = useState<Set<string>>(() => new Set())
  const [isLoading, setIsLoading] = useState(true)

  // Reset during render rather than from an effect when the caller points the hook at a new
  // user id (sign-in/sign-out). This mirrors the pattern in useInternResource.ts: it applies
  // before anything commits, so gated UI never flashes stale state for a render.
  const [requestedUserId, setRequestedUserId] = useState(userId)
  if (userId !== requestedUserId) {
    setRequestedUserId(userId)
    setIsLoading(true)
    setEnabled(new Set())
  }

  useEffect(() => {
    let active = true
    const controller = new AbortController()
    void (async () => {
      try {
        const response = await fetch('/api/features', { signal: controller.signal })
        const payload = response.ok ? await response.json() : { enabled: [] }
        if (!active) return
        setEnabled(new Set(Array.isArray(payload?.enabled) ? payload.enabled : []))
      } catch {
        if (!active) return
        setEnabled(new Set())
      } finally {
        if (active) setIsLoading(false)
      }
    })()
    return () => {
      active = false
      controller.abort()
    }
  }, [userId])

  const isEnabled = useCallback((key: string) => enabled.has(key), [enabled])
  return { isEnabled, isLoading }
}
